'use client';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Types as coreTypes } from '@ohif/core';
import { Search } from 'lucide-react';
import { fetchLogs } from './logs'; // Asegúrate de tener la función fetchLogs
import { Header, useModal, Onboarding, toast } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

function LogsPanel({ servicesManager }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [logs, setLogs] = useState([]); // Estado para los logs
  const [totalLogs, setTotalLogs] = useState(0); // Total de logs para paginación
  const [currentPage, setCurrentPage] = useState(1); // Página actual
  const [totalPages, setTotalPages] = useState(1); // Total de páginas para paginación
  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  // Función para cargar los logs
  const loadLogs = async () => {
    try {
      const fetchedLogs = await fetchLogs({
        search: searchTerm,
        sortBy: 'createdAt', // Puedes cambiar este valor según el campo por el que quieras ordenar
        order: 'desc', // Puedes cambiar esto a 'asc' si quieres ordenar ascendentemente
        limit: 25,
        page: currentPage,
      });
      setLogs(fetchedLogs.logs);
      setTotalLogs(fetchedLogs.totalLogs);
      setTotalPages(fetchedLogs.totalPages);
    } catch (error) {
      console.error('Error al cargar logs:', error);
    }
  };
  useEffect(() => {
    // Verificar si hay token en localStorage
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;

    if (!session?.token) {
      // No hay token: redirigir al login
      toast.warning('Hubo un error no tienes permisos para esta pagina');
      navigate('/');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    loadLogs();
  }, [searchTerm, currentPage]);

  // Función para cambiar de página
  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Panel de Logs</h1>
            <p className="mt-1">Gestiona los logs del sistema</p>
          </div>
        </div>

        {/* Buscador y lista */}
        <div className="bg-secondary-main mt-5 rounded shadow">
          <div className="flex flex-col justify-between gap-4 p-4 md:flex-row">
            <div>
              <h2 className="text-lg font-semibold">Logs ({logs.length})</h2>
              <p className="text-sm text-gray-400">Lista de actividades registradas</p>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="focus:ring-primary-active w-full rounded border border-gray-300 bg-black py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2"
                placeholder="Buscar logs..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 overflow-y-auto p-4">
            {logs.length > 0 ? (
              logs.map(log => (
                <div
                  key={log._id}
                  className="bg-secondary-dark border-primary-active gap-4 rounded border p-4 shadow max-sm:flex-col max-sm:gap-6 sm:flex sm:items-center sm:justify-between"
                >
                  <div>
                    <h3 className="text-md whitespace-normal break-words font-semibold">
                      Ruta: {log.route}
                    </h3>
                    <p className="text-sm text-gray-300">Acción: {log.action}</p>
                    <p className="text-xs text-gray-400">IP: {log.ipAddress}</p>
                    <p className="text-xs text-gray-400">User Agent: {log.userAgent}</p>
                    <p className="text-xs text-gray-400">
                      Timestamp: {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={`rounded px-2 py-1 text-xs uppercase ${log.userRole === 'admin' ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-800'}`}
                  >
                    {log.userName || 'Desconocido'}
                  </span>
                </div>
              ))
            ) : (
              <p className="py-8 text-center text-gray-400">No se encontraron logs</p>
            )}
          </div>

          {/* Paginación */}
          <div className="mt-4 flex justify-center">
            <button
              className="mr-2 rounded bg-gray-600 px-4 py-2 text-white"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span className="text-white">
              {currentPage} de {totalPages}
            </span>
            <button
              className="ml-2 rounded bg-gray-600 px-4 py-2 text-white"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
          <div className="bg-secondary-dark border-primary-active mt-8 gap-4 rounded border p-4 shadow max-sm:flex-col max-sm:gap-6 sm:hidden sm:items-center sm:justify-between">
            <div></div>
            <span></span>
            <span></span>
            <div className="flex gap-2"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function getHeaderConfig({ servicesManager, t, show, navigate, appConfig }) {
  const { customizationService } = servicesManager.services;

  const AboutModal = customizationService.getCustomization(
    'ohif.aboutModal'
  ) as coreTypes.MenuComponentCustomization;
  const UserPreferencesModal = customizationService.getCustomization(
    'ohif.userPreferencesModal'
  ) as coreTypes.MenuComponentCustomization;

  const menuOptions = [
    {
      title: AboutModal?.menuTitle ?? t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: AboutModal?.title ?? t('AboutModal:About MED VIEWER'),
          containerClassName: AboutModal?.containerClassName ?? 'max-w-md',
        }),
    },
    {
      title: UserPreferencesModal?.menuTitle ?? t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          content: UserPreferencesModal,
          title: UserPreferencesModal?.title ?? t('UserPreferencesModal:User preferences'),
          containerClassName:
            UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl p-6 flex-col',
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  return { menuOptions, appConfig };
}

LogsPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default LogsPanel;
